import React, { useState } from "react";

type ValueType = "plain" | "keyvault";

export interface PendingEntryDiff {
  key: string;
  value_type: ValueType;
  current_value?: string;
  new_value?: string;
  current_value_type?: ValueType;
}

export interface ConflictRef {
  request_id: string;
  submitted_by: string;
  submitted_at: string;
  region: string;
}

export interface PendingRequest {
  id: string;
  submitted_by: string;
  submitted_at: string;
  environment: string;
  region: string | null;
  status: string;
  entries: PendingEntryDiff[];
  conflicts?: ConflictRef[];
}

interface ApprovalQueueProps {
  requests: PendingRequest[];
  onApprove: (requestId: string) => Promise<void>;
  onReject: (requestId: string, reason: string) => Promise<void>;
  onRefresh: () => void;
}

const getEnvironmentBadge = (environment: string) => {
  switch (environment) {
    case "staging":
      return "bg-amber-100 text-amber-800";
    case "production":
      return "bg-rose-100 text-rose-800";
    default:
      return "bg-gray-100 text-gray-600";
  }
};

const getStatusBadge = (status: string) => {
  switch (status) {
    case "pending":
      return { style: "bg-indigo-100 text-indigo-800", label: "Pending" };
    case "approved":
      return { style: "bg-emerald-100 text-emerald-800", label: "Approved — applying..." };
    default:
      return { style: "bg-gray-100 text-gray-600", label: status };
  }
};

const formatRelativeTime = (iso: string) => {
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

const ApprovalQueue: React.FC<ApprovalQueueProps> = ({
  requests,
  onApprove,
  onReject,
  onRefresh,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [processingId, setProcessingId] = useState<string | null>(null);

  const toggleExpand = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const startReject = (id: string) => {
    setRejectingId(id);
    setRejectReason("");
  };

  const confirmReject = async (id: string) => {
    setProcessingId(id);
    try {
      await onReject(id, rejectReason.trim());
    } finally {
      setProcessingId(null);
      setRejectingId(null);
      setRejectReason("");
    }
  };

  return (
    <div className="overflow-hidden rounded-lg border border-indigo-100 shadow-lg bg-white">
      {/* Header */}
      <div className="px-5 py-4 bg-gradient-to-r from-indigo-50 to-blue-50 border-b border-indigo-100 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">Pending approvals</h3>
          <p className="text-sm text-gray-500">
            {requests.length} {requests.length === 1 ? "request" : "requests"} awaiting review
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 transition-colors duration-150"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Refresh
        </button>
      </div>

      <div className="divide-y divide-gray-100">
        {requests.length > 0 ? (
          requests.map((request) => {
            const isExpanded = expandedId === request.id;
            const isRejecting = rejectingId === request.id;
            const isApproved = request.status === "approved";
            const statusBadge = getStatusBadge(request.status);

            return (
              <div key={request.id} className="px-5 py-4">

                {/* Request header */}
                <div className="flex items-start justify-between">
                  <div className="flex items-start">
                    <div className="flex-shrink-0 h-8 w-8 bg-indigo-100 rounded-full text-indigo-700 flex items-center justify-center text-sm font-semibold">
                      {request.submitted_by.charAt(0).toUpperCase()}
                    </div>
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {request.submitted_by}
                        <span className="ml-2 text-xs font-normal text-gray-400">
                          {formatRelativeTime(request.submitted_at)}
                        </span>
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        {/* Environment badge */}
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full capitalize ${getEnvironmentBadge(request.environment)}`}>
                          {request.environment}
                        </span>
                        {/* Region badge */}
                        {request.region && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-rose-100 text-rose-800">
                            {request.region.toUpperCase()}
                          </span>
                        )}
                        {/* Status badge */}
                        <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${statusBadge.style}`}>
                          {statusBadge.label}
                        </span>
                        <span className="text-xs text-gray-400">
                          &middot; {request.entries.length} {request.entries.length === 1 ? "key" : "keys"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => toggleExpand(request.id)}
                    className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                  >
                    {isExpanded ? "Hide diff" : "View diff"}
                  </button>
                </div>

                {/* Conflicts */}
                {request.conflicts && request.conflicts.length > 0 && (
                  <div className="mt-3 flex items-start px-3 py-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-xs">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l6.518 11.59c.75 1.334-.213 2.987-1.743 2.987H3.482c-1.53 0-2.493-1.653-1.743-2.987L8.257 3.1zM11 14a1 1 0 11-2 0 1 1 0 012 0zm-1-2a1 1 0 01-1-1V7a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
                    </svg>
                    <span>
                      {request.conflicts.map((c, i) => (
                        <React.Fragment key={c.request_id}>
                          {i > 0 && "; "}
                          Also pending: {c.region?.toUpperCase() || c.region} change by {c.submitted_by},{" "}
                          {formatRelativeTime(c.submitted_at)}
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                )}
                {/* Diff view */}
                {isExpanded && (
                  <div className="mt-3 border border-gray-200 rounded-md divide-y divide-gray-100">
                    {request.entries.map((entry, idx) => {
                      const isNewKey = entry.current_value == null;
                      const typeChanged =
                        entry.current_value_type != null &&
                        entry.current_value_type !== entry.value_type;

                      return (
                        <div key={idx} className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Key</span>
                          <span className="text-sm font-mono text-gray-800">{entry.key}</span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-400 uppercase tracking-wide">Value</span>
                          {!isNewKey && (
                            <span className="line-through text-gray-400 text-sm mr-1">{entry.current_value}</span>
                          )}
                          <span className="font-medium text-gray-900 text-sm">{entry.new_value}</span>
                        </div>
                        {typeChanged && (
                          <div className="mt-1 text-xs text-amber-700">
                            Value type changing:{" "}
                            {entry.current_value_type === "keyvault" ? "Key Vault ref" : "Plain value"}
                            {" → "}
                            {entry.value_type === "keyvault" ? "Key Vault ref" : "Plain value"}
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                )}

                {/* Actions — hidden for approved requests since they're in flight */}
                {!isApproved && (
                  isRejecting ? (
                    <div className="mt-3 space-y-2">
                      <textarea
                        value={rejectReason}
                        onChange={(e) => setRejectReason(e.target.value)}
                        rows={2}
                        placeholder="Reason for rejecting (optional)"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500 focus:border-rose-500"
                      />
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => confirmReject(request.id)}
                          disabled={processingId === request.id}
                          className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-rose-600 text-white hover:bg-rose-700 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          {processingId === request.id ? (
                            <>
                              <svg className="animate-spin h-3.5 w-3.5 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                              </svg>
                              Rejecting...
                            </>
                          ) : (
                            "Confirm reject"
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={() => setRejectingId(null)}
                          disabled={processingId === request.id}
                          className="px-3 py-1 text-sm font-medium rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 flex space-x-2">
                      <button
                        type="button"
                        onClick={async () => {
                          setProcessingId(request.id);
                          try {
                            await onApprove(request.id);
                          } finally {
                            setProcessingId(null);
                          }
                        }}
                        disabled={processingId === request.id}
                        className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {processingId === request.id ? (
                          <>
                            <svg className="animate-spin h-3.5 w-3.5 mr-1.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                            </svg>
                            Approving...
                          </>
                        ) : (
                          <>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 111.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Approve
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => startReject(request.id)}
                        className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-md border border-rose-300 text-rose-700 bg-white hover:bg-rose-50"
                      >
                        Reject
                      </button>
                    </div>
                  )
                )}

                {/* Approved in-flight state */}
                {isApproved && (
                  <div className="mt-3 flex items-center gap-2 text-xs text-emerald-700">
                    <svg className="animate-spin h-3.5 w-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                    </svg>
                    Approved — agent is applying the change...
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <div className="px-6 py-10 text-center text-gray-500">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <p className="mt-1">No pending requests</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ApprovalQueue;